<template>
  <div class="sign-up__confirm sign-up__confirm--phone sign-up__confirm--big">
    <figure class="sign-up__confirm__figure">
      <img
        src="/images/registration/sms–illustration.svg"
        alt="Precisamos que você confirme seu celular"
      />
    </figure>

    <h2 class="sign-up__confirm__title font-bold">
      Confirme seu celular.
    </h2>

    <p class="sign-up__confirm__description">
      Por favor, digite o código de 6 dígitos que enviamos para o número
      <span class="font-bold">{{ iptCelular }}</span>.
      <button class="sign-up__confirm__action" @click="toggleModal">Alterar número.</button>
    </p>

    <form class="sign-up__confirm__form">
      <div class="sign-up__confirm__form-content">
        <OTPInput
          :digits-count="6"
          :has-error="invalidCode"
          error-message="Por favor, verifique o código digitado."
          @full-filled="validateCode"
        />
      </div>
    </form>

    <div class="sign-up__confirm__warning">
      <p>Não recebeu o código?</p>
      <template v-if="!displayCountdown">
        <button class="sign-up__confirm__action" @click="sendConfirmationCode('whatsapp', true)">Reenviar por WhatsApp</button>
        <button class="sign-up__confirm__action" @click="sendConfirmationCode('sms', true)">Reenviar por SMS</button>
      </template>

      <small v-else class="text-neutral-clear">
        Reenviar o código em
        <VueCountdown
          v-slot="{ minutes, seconds }"
          :time="60000"
          :transform="formatValuesLowerThanZero"
          @end="displayCountdown = false"
        >
          {{ minutes }}:{{ seconds }}
        </VueCountdown>
      </small>
    </div>

    <transition name="fade">
      <div class="formConfirm formConfirm--phone" v-if="validated">
        <div class="wrapper">
          <svg class="ico">
            <use xlink:href="/images/svg-symbols.svg#form-ok"></use>
          </svg>
          <p>Seu celular foi verificado com sucesso!</p>
          <span>Clique em avançar para prosseguir com a confirmação dos seus dados.</span>
        </div>
        <div class="buttons">
          <a class="lnk btnPink btnAvancar" href="javascript:;" @click="$parent.next()">
            <span>avançar</span>
            <svg class="ico">
              <use xlink:href="/images/svg-symbols.svg#arrow-small"></use>
            </svg>
          </a>
        </div>
      </div>
    </transition>

    <transition name="fade">
      <modal-base
        title="Confirmação de celular"
        v-show="resend"
        @close-modal="toggleResendModal(false)"
      >
        <template v-slot:content>
          <div>
            <p v-if="channel === 'sms'" class="text-center">
              O código foi reenviado por SMS para o número<br>
              <span class="font-bold">{{ iptCelular }}</span>.
            </p>
            <p v-if="channel === 'whatsapp'" class="text-center">
              O código foi reenviado por WhatsApp para o número<br>
              <span class="font-bold">{{ iptCelular }}</span>.
            </p>
          </div>
        </template>

        <template v-slot:footer>
          <div>
            <button
              class="modal__footer__button modal__footer__button--action"
              @click.prevent="toggleResendModal(false)"
            >
              ENTENDI
            </button>
          </div>
        </template>
      </modal-base>
    </transition>

    <transition name="fade">
      <modal-base
        title="Confirmação de celular"
        v-show="blocked"
        @close-modal="toggleBlockedModal(false)"
      >
        <template v-slot:content>
          <p>
            Você atingiu o limite de tentativas,
            favor entrar em contato com nosso suporte!
          </p>
        </template>

        <template v-slot:footer>
          <div>
            <a :href="supportPublicDashUrl" class="modal__footer__button modal__footer__button--action">
              SUPORTE
            </a>
          </div>
        </template>
      </modal-base>
    </transition>

    <transition name="fade">
      <modal-base
        title="Alterar número de celular"
        v-show="isModalVisible"
        @close-modal="toggleModal"
      >
        <template v-slot:content>
          <div>
            <fieldset
              class="formLine"
              :class="{
                active: iptCelularChange,
                error: $v.iptCelularChange.$error,
                focus: focus.iptCelularChange,
              }"
            >
              <div class="inputBox">
                <label for="phone">Novo celular*</label>
                <input
                  type="tel"
                  id="phone"
                  v-mask="'(99) 99999-9999'"
                  v-model="iptCelularChange"
                  @focus="iptFocus('iptCelularChange', $event)"
                  @blur="iptBlur('iptCelularChange', $event)"
                />
              </div>
            </fieldset>

            <fieldset
              class="formLine"
              :class="{
                active: iptCelularChangeConfirmation,
                error: $v.iptCelularChangeConfirmation.$error,
                focus: focus.iptCelularChangeConfirmation,
              }"
            >
              <div class="inputBox">
                <label for="replace-phone">Confirmar novo celular*</label>
                <input
                  type="tel"
                  id="replace-phone"
                  v-mask="'(99) 99999-9999'"
                  v-model="iptCelularChangeConfirmation"
                  @focus="iptFocus('iptCelularChangeConfirmation', $event)"
                  @blur="iptBlur('iptCelularChangeConfirmation', $event)"
                />
              </div>
            </fieldset>
          </div>
        </template>

        <template v-slot:footer>
          <div>
            <button class="modal__footer__button" @click.prevent="toggleModal">
              Cancelar
            </button>
            <button
              class="modal__footer__button modal__footer__button--action"
              @click.prevent="updateMobilePhone"
            >
              Alterar
            </button>
          </div>
        </template>
      </modal-base>
    </transition>

    <toast ref="toast" :message="message" :time="3000"/>
  </div>
</template>

<script>
import phoneMobile from "commons/js/validators/phoneMobile";
import smsCode from "commons/js/validators/smsCode";
import AwesomeMask from "awesome-mask";
import ModalBase from "commons/js/components/atoms/ModalBase.vue";
import Toast from 'commons/js/components/atoms/Toast.vue';
import {required, sameAs} from "vuelidate/lib/validators";

import {scrollToTop} from 'commons/js/utils/scroll.js';

import OTPInput from "commons/js/components/atoms/OTPInput";

import VueCountdown from "@chenfengyuan/vue-countdown";
import {formatValuesLowerThanZero} from "commons/js/config/vue-countdown";

export default {
  props: {
    me: {
      type: Object,
      required: true,
    },
  },

  components: {
    ModalBase,
    Toast,
    OTPInput,
    VueCountdown
  },

  data() {
    return {
      displayCountdown: false,
      invalidCode: false,
      resend: false,
      channel: '',
      blocked: false,
      validated: false,
      isModalVisible: false,
      timerActive: false,
      iptCelular: "",
      iptCelularChange: "",
      iptCelularChangeConfirmation: "",
      iptCodValidacao: "",
      code: "",
      feedback: "",
      message: "",
      errorMessages: {
        required: "Campo obrigatório! Não esqueça de preenchê-lo.",
        phoneMobile: "Número de celular inválido! Verifique sua resposta.",
        smsCode: "Código inválido! Informe os 6 digitos.",
        isUnique: "Este __field__ já está cadastrado ou é inválido. Gostaria de fazer login?",
        sameAsIptCelularChangeConfirmation: "O número não é idêntico ao informado no campo \"Novo celular\".",
      },
      errors: {
        iptCodValidacao: "",
        iptCelularChange: "",
        iptCelularChangeConfirmation: "",
      },
      focus: {
        iptCodValidacao: false,
        iptCelularChange: false,
        iptCelularChangeConfirmation: false,
      },
      supportPublicDashUrl: ''
    };
  },

  directives: {
    mask: AwesomeMask,
  },

  created() {
    this.setPhone();
    this.sendConfirmationCode('sms');
  },

  watch: {
    me: function () {
      this.setPhone();
    },
  },

  mounted() {
    scrollToTop();
    setTimeout(this.$v.$reset, 0);
    this.supportPublicDashUrl = MEConfig.get('dash.url') + '/suporte';
  },

  methods: {
    formatValuesLowerThanZero,

    setPhone() {
      if (this.me.phone && this.me.phone.phone) {
        this.iptCelular = String(this.me.phone.phone).replace(
          /^(\d{2})(\d{5})(\d{4})$/,
          "($1) $2-$3"
        );
      }
    },
    iptFocus(ipt) {
      this.focus[ipt] = true;
    },
    iptBlur(ipt) {
      this.focus[ipt] = false;
      if (ipt !== 'iptCodValidacao') {
        this.$v[ipt].$touch();
      }
      this.updateErrorMessage(ipt);
    },
    validateCode({code}) {
      if (this.timerActive) {
        this.message = 'Aguarde para tentar novamente.';
        this.$refs.toast.show();

        return;
      }

      if (this.resend && !this.timerActive) {
        this.initTimer();
      }

      this.confirmCode(code);
    },

    toggleResendModal(visible, channel) {
      this.resend = visible;
      this.channel = channel;
      this.$parent.showModal = visible;
    },
    toggleBlockedModal(visible) {
      this.blocked = visible;
      this.$parent.showModal = visible;
    },
    hideAllModals() {
      this.resend = false;
      this.blocked = false;
    },
    sendConfirmationCode(channel = 'sms', resend = false) {
      if (resend) {
        this.displayCountdown = true;
      }

      this.$parent.loading = true;
      axios.post("/api/v2/me/send/phone-confirmation-code", {
          channel: channel,
          resend: resend
        })
        .then(() => {
          if (resend) {
            this.toggleResendModal(true, channel);
          }
        })
        .catch((error) => {
          if (resend && error.response.status === 403) {
            this.toggleBlockedModal(true);
          }
        })
        .finally(() => {
          this.$parent.loading = false;
        });
    },
    updateErrorMessage(key) {
      let errorTypes = Object.keys(this.errorMessages);
      for (let i = 0; i < errorTypes.length; i++) {
        let errorType = errorTypes[i];
        if (
          this.$v[key].hasOwnProperty(errorType) &&
          !this.$v[key][errorType]
        ) {
          this.errors[key] = "";
          if (this.errorMessages.hasOwnProperty(errorType)) {
            this.errors[key] = String(this.errorMessages[errorType]).replace(
              /__field__/,
              String(key)
                .replace(/^ipt/, "")
                .replace(/Change$/, "")
            );
          }
          break;
        }
      }
    },

    updateMobilePhone() {
      if (this.$v.iptCelularChange.$invalid || this.$v.iptCelularChangeConfirmation.$invalid) {
        this.$v.$touch();
        this.updateErrorMessage("iptCelularChange");
        this.updateErrorMessage("iptCelularChangeConfirmation");
        return;
      }

      axios
        .put("/api/v2/me", {
          phone: String(this.iptCelularChange).replace(/\D/g, ""),
        })
        .then(() => {
          this.iptCelular = this.iptCelularChange;
          this.sendConfirmationCode('sms');
          this.toggleModal();
          this.message = 'Alterado com sucesso';
          this.$refs.toast.show();
        })
        .catch((error) => {
          this.toggleModal();
          this.message = error.response.data.error || 'Ocorreu um erro';
          this.$refs.toast.show();
        });
    },

    confirmCode(value) {
      this.invalidCode = false

      return new Promise((resolve) => {
        this.$parent.loading = true;

        axios.post("/api/v2/me/verify/phone-confirmation", {code: value})
          .then((response) => {
            if (response.data && response.data.status) {
              window.location.href = "/cadastre-se/boas-vindas";
              resolve(true);
              return;
            }

            this.invalidCode = true
            resolve(false);
          })
          .catch(() => {
            resolve(false);
          })
          .finally(() => {
            this.$parent.loading = false;
          });
      });
    },

    initTimer() {
      let time = 30000;

      this.timerActive = true;

      window.setTimeout(() => this.finishTimer(), time);
    },

    finishTimer() {
      this.timerActive = false;
    },

    toggleModal() {
      this.isModalVisible = !this.isModalVisible;
    }
  },

  validations: {
    iptCelular: {
      phoneMobile,
      isUnique(value) {
        if (!value) return true;
        value = String(value).replace(/\D/g, "");
        if (value.length !== 11) return false;
        if (!this.me) return false;
        return new Promise((resolve) => {
          axios
            .get("/api/v2/validate/phone/" + value + "?ignore=" + this.me.id)
            .then((response) => {
              resolve(response.data);
            })
            .catch(() => {
              resolve(false);
            });
        });
      },
    },

    iptCelularChange: {
      required,
    },

    iptCelularChangeConfirmation: {
      required,
      sameAsIptCelularChangeConfirmation: sameAs("iptCelularChange"),
    },

    iptCodValidacao: {
      smsCode
    },
  }
};
</script>


<style lang="stylus" scoped>
$theme-danger = #E7505A

.sign-up
  &__confirm
    &__warning
      display flex
      flex-direction column
      justify-content center
      gap 16px
      color #26303c

.invalid-code
  border 1px solid red
  border-radius 4px
  border 1px solid $theme-danger
  margin-top 36px

  &__content
    font-size 12px
    color $theme-danger
    text-align left
    padding 12px 16px
    display flex
    align-items center

  &__icon
    width 14px
    height 14px
    margin-right 12px

.modal
  &__content
    p
      text-align center !important

  &__footer
    a
      text-decoration none

</style>
